import { useEffect, useState } from "react"

import type { UseQueryResult } from "@tanstack/react-query"
import { selector, useRecoilValue } from "recoil"

import type { ProposalSavingsDto } from "~/api/dto/proposal.dto"
import useFetchProposalSavings from "~/api/hooks/useFetchProposalSavings"
import { useGetLightReachQuote } from "~/api/hooks/useGetLightReachQuotes"
import { usePublicPresentation } from "~/containers/PublicPresentation/public-presentation-context"
import { FinanceType } from "~/utils/finance/constants"

import { proposalLocalState } from "../proposal/proposal-state"
import {
  quotesRemoteState,
  quotesSnapshotSettledScopeState,
} from "../quotes/quotes-state"
import { syncWaitingForEntityState } from "../sync-state"
import {
  EMPTY_PROPOSAL_SAVINGS,
  getProposalSavingsGate,
  PROPOSAL_SAVINGS_QUOTE_WAIT_MS,
  type ProposalSavingsGate,
} from "./proposal-savings-gate"

const FINANCE_TYPES_REQUIRING_QUOTES = new Set<string>([
  FinanceType.CONCERT,
  FinanceType.CREDIT_HUMAN,
  FinanceType.ENFIN,
  FinanceType.GOODLEAP,
  FinanceType.LIGHT_REACH,
  FinanceType.SUNGAGE,
])

const proposalSavingsQuoteMatchState = selector<{
  hasMatchingUnpoisonedQuote: boolean
  requiresQuote: boolean
}>({
  key: `proposalSavingsQuoteMatch`,
  get: ({ get }) => {
    const { id, financeType, selectedLoanProduct } = get(proposalLocalState)
    const effectiveFinanceType = financeType ?? FinanceType.CASH
    const requiresQuote =
      FINANCE_TYPES_REQUIRING_QUOTES.has(effectiveFinanceType)

    if (!requiresQuote) {
      return { requiresQuote: false, hasMatchingUnpoisonedQuote: false }
    }

    if (!selectedLoanProduct?.id) {
      return { requiresQuote: true, hasMatchingUnpoisonedQuote: false }
    }

    return {
      requiresQuote: true,
      hasMatchingUnpoisonedQuote: get(quotesRemoteState).some(
        (quote) =>
          quote.__typename === `Quote` &&
          quote.proposalId === id &&
          quote.loanProductId === selectedLoanProduct.id,
      ),
    }
  },
})

export const proposalSavingsQueryScopeState = selector<string>({
  key: `proposalSavingsQueryScope`,
  get: ({ get }) => {
    const { financeProductId, financeType, fundingGroup, selectedLoanProduct } =
      get(proposalLocalState)

    return [
      financeType ?? FinanceType.CASH,
      fundingGroup ?? ``,
      financeProductId ?? ``,
      selectedLoanProduct?.id ?? ``,
    ].join(`:`)
  },
})

const proposalSavingsQuotesSnapshotSettledState = selector<boolean>({
  key: `proposalSavingsQuotesSnapshotSettled`,
  get: ({ get }) => {
    return (
      get(quotesSnapshotSettledScopeState) ===
      get(proposalSavingsQueryScopeState)
    )
  },
})

const proposalSavingsLightReachSelectionState = selector<{
  financeProductId: string | null
  isLightReach: boolean
}>({
  key: `proposalSavingsLightReachSelection`,
  get: ({ get }) => {
    const { financeProductId, financeType } = get(proposalLocalState)

    return {
      financeProductId,
      isLightReach: financeType === FinanceType.LIGHT_REACH,
    }
  },
})

const asSettledEmptyProposalSavings = (
  query: UseQueryResult<ProposalSavingsDto, unknown>,
): UseQueryResult<ProposalSavingsDto, unknown> =>
  ({
    ...query,
    data: EMPTY_PROPOSAL_SAVINGS,
    error: null,
    fetchStatus: `idle`,
    isError: false,
    isFetching: false,
    isLoading: false,
    isRefetching: false,
    isSuccess: true,
    status: `success`,
  }) as unknown as UseQueryResult<ProposalSavingsDto, unknown>

const useProposalSavingsWaitTimedOut = (
  gateWithoutTimeout: ProposalSavingsGate,
  queryScope: string,
): boolean => {
  const [timedOutScope, setTimedOutScope] = useState<string | null>(null)

  useEffect(() => {
    if (gateWithoutTimeout !== `wait`) return

    const timeoutId = window.setTimeout(() => {
      setTimedOutScope(queryScope)
    }, PROPOSAL_SAVINGS_QUOTE_WAIT_MS)

    return () => window.clearTimeout(timeoutId)
  }, [gateWithoutTimeout, queryScope])

  return timedOutScope === queryScope
}

export const useProposalSavings = (
  proposalId: string,
): ReturnType<typeof useFetchProposalSavings> => {
  const presentation = usePublicPresentation()
  const { hasMatchingUnpoisonedQuote, requiresQuote } = useRecoilValue(
    proposalSavingsQuoteMatchState,
  )
  const queryScope = useRecoilValue(proposalSavingsQueryScopeState)
  const quotesSyncWaiting = useRecoilValue(syncWaitingForEntityState(`quotes`))
  const quotesSnapshotSettled = useRecoilValue(
    proposalSavingsQuotesSnapshotSettledState,
  )
  const { financeProductId, isLightReach } = useRecoilValue(
    proposalSavingsLightReachSelectionState,
  )
  const lightReachQuote = useGetLightReachQuote(
    proposalId,
    isLightReach && !!financeProductId,
  )

  const lightReachHasMatchingQuote =
    lightReachQuote.data?.isValid === true &&
    lightReachQuote.data.productId === financeProductId
  const lightReachSnapshotSettled =
    (lightReachQuote.isFetched || lightReachQuote.isError) &&
    (lightReachQuote.data == null ||
      lightReachQuote.data.productId === financeProductId ||
      lightReachQuote.data.isValid !== true)

  const gateInput =
    isLightReach && financeProductId
      ? {
          requiresQuote: true,
          hasMatchingUnpoisonedQuote: lightReachHasMatchingQuote,
          quotesSyncWaiting:
            lightReachQuote.isFetching && !lightReachHasMatchingQuote,
          quotesSnapshotSettled: lightReachSnapshotSettled,
        }
      : {
          requiresQuote,
          hasMatchingUnpoisonedQuote,
          quotesSyncWaiting,
          quotesSnapshotSettled,
        }

  const gateWithoutTimeout = getProposalSavingsGate({
    ...gateInput,
    waitTimedOut: false,
  })
  const waitTimedOut = useProposalSavingsWaitTimedOut(
    gateWithoutTimeout,
    queryScope,
  )
  const gate = getProposalSavingsGate({
    ...gateInput,
    waitTimedOut,
  })

  const query = useFetchProposalSavings(proposalId, {
    enabled: gate === `fetch`,
    queryScope: `${queryScope}:${gate}`,
  })

  if (!presentation && gate === `settle`) {
    return asSettledEmptyProposalSavings(query)
  }

  return query
}

export default useProposalSavings
